import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ProjectTargetInput, ProjectResponse } from "@api/apiClient";
import { useUpdateProjectBenchmarkSettings } from "@api/projects";
import {
  useBenchmarkCatalogSummaries,
  useBenchmarkCatalog,
  useBenchmarkAndTargets,
} from "@api/benchmarkCatalog";
import i18n from "@i18n/i18n";

interface BenchmarkTargetsFormProps {
  projectId?: string;
  project?: ProjectResponse | undefined;
  onSaveSuccess?: (message: string) => void;
  onSaveError?: (message: string) => void;
  onBenchmarkDataChange?: (data: { catalogId: string; targets: ProjectTargetInput[] }) => void;
}

export const BenchmarkTargetsForm = ({
  projectId,
  project,
  onSaveSuccess,
  onSaveError,
  onBenchmarkDataChange,
}: BenchmarkTargetsFormProps) => {
  const isNewProjectMode = !projectId;
  const theme = useTheme();

  const linkedCatalogId: string | undefined =
    (project?.project as any)?.benchmarkCatalogEntityId ?? undefined;

  // `userPickedCatalogId` is null until the dropdown is changed; until then we
  // derive the effective ID directly from the project's linked catalog so we
  // never wait for a useEffect round-trip to populate the query param.
  const [userPickedCatalogId, setUserPickedCatalogId] = useState<string | null>(null);
  const effectiveCatalogId = userPickedCatalogId ?? linkedCatalogId ?? "";

  const [targetEdits, setTargetEdits] = useState<Record<string, string>>({});
  const [hasTargetChanges, setHasTargetChanges] = useState(false);

  const updateBenchmarkSettings = useUpdateProjectBenchmarkSettings(projectId ?? "");

  const { data: catalogSummaries = [] } = useBenchmarkCatalogSummaries({
    enabled: true,
  });
  const { data: catalogDetail } = useBenchmarkCatalog(
    effectiveCatalogId || null,
    { enabled: !!effectiveCatalogId }
  );
  const { data: benchmarkAndTargets } = useBenchmarkAndTargets(
    projectId ?? "",
    { enabled: !!projectId }
  );

  // Reset user override when the project's persisted catalog changes (e.g. after save).
  useEffect(() => {
    setUserPickedCatalogId(null);
    setHasTargetChanges(false);
  }, [linkedCatalogId]);

  // Populate target inputs from the project's SAVED target values.
  useEffect(() => {
    if (!benchmarkAndTargets) return;
    const init: Record<string, string> = {};
    for (const t of benchmarkAndTargets.targetValues ?? []) {
      const key = t.modelTypeEntityId ?? "WholeBuilding";
      init[key] = String(t.valueKgCo2ePerM2);
    }
    setTargetEdits(init);
    setHasTargetChanges(false);
  }, [benchmarkAndTargets]);

  // When the user selects a DIFFERENT catalog, pre-fill the target inputs from
  // that catalog's benchmark values so they have a sensible starting point.
  useEffect(() => {
    if (!catalogDetail || effectiveCatalogId === linkedCatalogId) return;
    const init: Record<string, string> = {};
    for (const v of catalogDetail.values ?? []) {
      if (v.isNew) continue;
      const key = v.modelTypeEntityId ?? "WholeBuilding";
      init[key] = String(v.valueKgCo2ePerM2);
    }
    setTargetEdits(init);
  }, [catalogDetail, effectiveCatalogId, linkedCatalogId]);

  const buildTargets = (edits: Record<string, string>): ProjectTargetInput[] =>
    (catalogDetail?.values ?? [])
      .filter((v) => !v.isNew)
      .map((v) => {
        const key = v.modelTypeEntityId ?? "WholeBuilding";
        return new ProjectTargetInput({
          scope: v.scope,
          modelTypeEntityId: v.modelTypeEntityId ?? undefined,
          valueKgCo2ePerM2: parseFloat(edits[key] ?? "0") || 0,
        });
      });

  const notifyParent = (catalogId: string, edits: Record<string, string>) => {
    onBenchmarkDataChange?.({ catalogId, targets: buildTargets(edits) });
  };

  const handleCatalogChange = (newCatalogId: string) => {
    setUserPickedCatalogId(newCatalogId);
    setHasTargetChanges(true);
    if (isNewProjectMode) {
      notifyParent(newCatalogId, targetEdits);
    }
  };

  const handleTargetEdit = (key: string, value: string) => {
    const updatedEdits = { ...targetEdits, [key]: value };
    setTargetEdits(updatedEdits);
    setHasTargetChanges(true);
    if (isNewProjectMode) {
      notifyParent(effectiveCatalogId, updatedEdits);
    }
  };

  // In new-project mode, notify parent when catalog detail loads and targets are pre-filled.
  useEffect(() => {
    if (!isNewProjectMode || !catalogDetail || !effectiveCatalogId) return;
    notifyParent(effectiveCatalogId, targetEdits);
  }, [catalogDetail]);

  const handleSave = async () => {
    const targets = buildTargets(targetEdits);

    try {
      await updateBenchmarkSettings.mutateAsync({
        catalogId: effectiveCatalogId || null,
        projectTargets: targets,
      });
      setHasTargetChanges(false);
      onSaveSuccess?.(i18n.t("project-benchmark.targets-saved"));
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || i18n.t("project-benchmark.targets-save-error");
      onSaveError?.(message);
    }
  };

  const benchmarkValues = (catalogDetail?.values ?? []).filter((v) => !v.isNew);

  return (
    <Card elevation={3} sx={{ "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default } }}>
      <CardHeader
        title={
          <Typography variant="h6" fontWeight="bold">
            {i18n.t("project-benchmark.section-benchmark")}
          </Typography>
        }
      />
      <CardContent sx={{ borderTop: 1, borderColor: theme.palette.divider }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{i18n.t("project-benchmark.catalog-dropdown-label")}</InputLabel>
          <Select
            value={effectiveCatalogId}
            label={i18n.t("project-benchmark.catalog-dropdown-label")}
            onChange={(e) => handleCatalogChange(e.target.value)}
          >
            <MenuItem value="">
              <em>{i18n.t("project-benchmark.catalog-none")}</em>
            </MenuItem>
            {catalogSummaries.map((c) => (
              <MenuItem key={c.metadataEntityId} value={c.metadataEntityId}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {catalogDetail && benchmarkValues.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {i18n.t("project-benchmark.section-benchmark")} ({i18n.t("project-benchmark.unit")})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("benchmark-catalog-dialog.col-scope")}</TableCell>
                  <TableCell align="right">{i18n.t("benchmark-catalog-dialog.col-value")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {benchmarkValues.map((v) => (
                  <TableRow key={`bm-${v.metadataEntityId}`}>
                    <TableCell>
                      {v.scope === "WholeBuilding"
                        ? i18n.t("project-benchmark.whole-building")
                        : v.modelTypeName}
                    </TableCell>
                    <TableCell align="right">{v.valueKgCo2ePerM2}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
          {i18n.t("project-benchmark.section-targets")}
        </Typography>
        {catalogDetail && benchmarkValues.length > 0 && (
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("benchmark-catalog-dialog.col-scope")}</TableCell>
                <TableCell sx={{ width: 220 }}>{i18n.t("benchmark-catalog-dialog.col-value")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {benchmarkValues.map((v) => {
                const key = v.modelTypeEntityId ?? "WholeBuilding";
                return (
                  <TableRow key={`tgt-${v.metadataEntityId}`}>
                    <TableCell>
                      {v.scope === "WholeBuilding"
                        ? i18n.t("project-benchmark.whole-building")
                        : v.modelTypeName}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={targetEdits[key] ?? ""}
                        onChange={(e) => handleTargetEdit(key, e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">kgCO₂e/m²</InputAdornment>
                          ),
                        }}
                        sx={{ width: 180 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {!isNewProjectMode && hasTargetChanges && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSave}
              loading={updateBenchmarkSettings.isPending}
            >
              {i18n.t("project-benchmark.save-targets")}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
